import {Box, Loader} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import DOMPurify from 'dompurify';
import React, {useEffect} from 'react';

import {generateReactionImage} from 'chemistry/ChemistryDAO';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import useResizeSVG from 'ui-library/hooks/useResizeSVG';

// Note: could port this over to use chemicalStructureFromFile
async function fetchMarvinContent(
  externalFileId: string,
  externalFileUrl: string,
  entryId: string
): Promise<string | null> {
  const signedUrl = await performSignedFileQuery(externalFileId, externalFileUrl, entryId);
  if (signedUrl != null) {
    const response = await fetch(signedUrl);
    if (response.ok) {
      return await response.text();
    } else {
      throw new Error('Failed to fetch Marvin file content');
    }
  }
  return null;
}

export default function ReactionSchemeContent({
  externalFileId,
  externalFileUrl,
  entryId,
  setMarvinStructureContent,
}: {
  externalFileId: string;
  externalFileUrl: string;
  entryId: string;
  setMarvinStructureContent: (marvinStructureContent: string | null) => void;
}): JSX.Element {
  const reactionSVGRef = useResizeSVG<HTMLDivElement>(css(styles.reactionSVG));

  const {currentState: marvinContentAsyncState, makeCall: fetchMarvinContentAsync} = useAsyncState<
    [string, string, string],
    string | null
  >({makeCall: fetchMarvinContent});
  const {currentState: reactionBase64SVGAsyncState, makeCall: generateReactionImageAsync} = useAsyncState({
    makeCall: generateReactionImage,
  });

  useEffect(() => {
    fetchMarvinContentAsync(externalFileId, externalFileUrl, entryId);
  }, [externalFileId, externalFileUrl, entryId, fetchMarvinContentAsync]);

  const currentMarvinStructureContent =
    marvinContentAsyncState.status === AsyncStatus.SUCCESS && marvinContentAsyncState.data != null
      ? marvinContentAsyncState.data
      : null;

  useEffect(() => {
    setMarvinStructureContent(currentMarvinStructureContent);
    if (currentMarvinStructureContent != null) {
      generateReactionImageAsync(currentMarvinStructureContent);
    }
  }, [generateReactionImageAsync, currentMarvinStructureContent, setMarvinStructureContent]);

  if (
    marvinContentAsyncState.status === AsyncStatus.LOADING ||
    reactionBase64SVGAsyncState.status === AsyncStatus.LOADING
  ) {
    // TODO: disable cursor while loading
    return <Loader size="xl" />;
  } else if (
    marvinContentAsyncState.status === AsyncStatus.SUCCESS &&
    marvinContentAsyncState.data != null &&
    reactionBase64SVGAsyncState.status === AsyncStatus.SUCCESS
  ) {
    return (
      <Box
        ref={reactionSVGRef}
        role="img"
        aria-label="Reaction diagram"
        w="100%"
        h="100%"
        p="12px"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(atob(reactionBase64SVGAsyncState.data.base64SVG)),
        }}
      />
    );
  } else {
    // TODO: Use design-approved error state UI
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load reaction scheme" />;
  }
}

const styles = StyleSheet.create({
  // Ensure SVG scales proportionally within the container. useResizeSVG expects a single className arg so
  // define this style but prefer Mantine props otherwise.
  reactionSVG: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
});
